import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  type Signal,
} from '@angular/core';
import { Money } from '@principle-theorem/accounting';
import { WarningComponent } from '@principle-theorem/ng-principle-shared';
import {
  DepositStatus,
  isUnpaidDepositStatus,
  type IAppointment,
  type IDepositMetadata,
} from '@principle-theorem/principle-core/interfaces';
import { type WithRef } from '@principle-theorem/shared';

@Component({
  selector: 'pr-appointment-deposit-warning-sidebar',
  templateUrl: './appointment-deposit-warning-sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [WarningComponent],
})
export class AppointmentDepositWarningSidebarComponent {
  readonly appointment = input<WithRef<IAppointment>>();

  /**
   * Gated on `isUnpaidDepositStatus` so this alert and every other "you still
   * owe us" affordance agree on what counts as outstanding. `Authorised` is
   * settled money — the funds are held and will be captured — so chasing it
   * would be wrong.
   */
  readonly depositWarning: Signal<string | undefined> = computed(() => {
    const deposit = this.appointment()?.deposit?.metadata;
    if (!deposit || !isUnpaidDepositStatus(deposit.status)) {
      return;
    }
    return this._unpaidWarning(deposit);
  });

  /**
   * The two unpaid statuses need different actions, so they cannot share copy:
   * `Required` means nobody has asked the patient yet, while `Pending` means
   * the request is out and the patient is the one to chase.
   */
  private _unpaidWarning(deposit: IDepositMetadata): string {
    const amount = Money.formatPlainCents(
      deposit.amountCents,
      deposit.currency
    );
    if (deposit.status === DepositStatus.Required) {
      return `Deposit of ${amount} is required and has not been requested`;
    }
    return `Deposit of ${amount} is awaiting payment`;
  }
}
